import { useState } from "react";
import { Button, Card, DataList, PageHeader, StatusBadge, type DataColumn } from "@nahal/ui";
import type { AuditEvent } from "@nahal/api-client";
import { useAudit } from "../../lib/queries";

/**
 * The organisation's own slice of the platform audit trail -- the same events
 * ops sees, filtered to this customer by the token's organisation. Support and
 * the customer reading the same record is what stops "we never changed that"
 * from becoming an argument.
 */
export function ActivityScreen() {
  const [page, setPage] = useState(0);
  const audit = useAudit(page);

  const columns: Array<DataColumn<AuditEvent>> = [
    { key: "action", header: "What", primary: true, cell: (row) => readable(row.action) },
    {
      key: "actor",
      header: "Who",
      cell: (row) => (row.actorType === "STAFF" ? "Nahal Labs" : (row.actorLabel ?? "you")),
    },
    { key: "when", header: "When", cell: (row) => new Date(row.createdAt).toLocaleString() },
    { key: "summary", header: "Detail", desktopOnly: true, cell: (row) => row.summary ?? "-" },
  ];

  const total = audit.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title="Activity"
        description="Every change to your organisation, by you or by us."
        meta={<StatusBadge status={`page ${page + 1} of ${Math.max(total, 1)}`} tone="neutral" />}
      />

      <Card>
        <DataList
          columns={columns}
          rows={audit.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={audit.isLoading}
          error={audit.isError ? "Could not load your activity." : null}
          empty={{
            title: "Nothing yet",
            description: "Changes to your organisation, members and workspaces show up here.",
          }}
        />
      </Card>

      <div className="mt-3 flex items-center justify-between gap-2">
        <Button variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <Button variant="outline" disabled={page + 1 >= total} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
      </div>
    </>
  );
}

/** `member.role_change` reads as machine output; a customer should see words. */
function readable(action: string) {
  const labels: Record<string, string> = {
    "organization.update": "Organisation details updated",
    "member.add": "Member added",
    "member.remove": "Member removed",
    "member.role_change": "Member role changed",
    "provisioning.request": "Request submitted",
    "provisioning.approved": "Request approved",
    "provisioning.rejected": "Request rejected",
    "provisioning.completed": "Request completed",
    "workspace.provision": "Workspace provisioned",
    "workspace.status": "Workspace status changed",
    "workspace.entitlements": "Workspace limits changed",
    "support.note": "Support note added",
  };
  return labels[action] ?? action;
}
